// High-intent SEO guide pages. Each renders at a clean top-level route via <GuidePage />.
// Content is educational only: no diagnosis or treatment. Every guide attaches sources.

import type { ArticleCategory } from "./articles";

export interface GuideSection {
  h: string;
  body?: string;
  points?: string[];
}

export interface Guide {
  slug: string;
  /** Where it sits on /resources. */
  topic: ArticleCategory;
  /** browser <title> */
  metaTitle: string;
  metaDescription: string;
  /** on-page H1 */
  h1: string;
  eyebrow: string;
  keywords: string[];
  /** answer-first opening paragraph */
  intro: string;
  sections: GuideSection[];
  faq: { q: string; a: string }[];
  sourceIds: string[];
  /**
   * Other guides by slug, or one of the site pages in RELATED_PAGES by path
   * ("/alpha-gal"). Paths cover the guides that were folded into a fuller page.
   */
  relatedSlugs: string[];
  lastReviewed: string;
}

/** The pages a guide's related list may name by path, with the line it shows. */
export const RELATED_PAGES = {
  "/alpha-gal": "Alpha-gal syndrome: the tick-linked meat allergy",
  "/tick-identification": "Identify a tick: Lone Star, deer or dog tick",
  "/tick-bite-guide": "What to do after a tick bite: a printable plan",
  "/diseases/ehrlichiosis": "Ehrlichiosis: the most common Lone Star tick illness",
  "/diseases/stari": "STARI: the Lone Star tick rash",
} as const;
export type RelatedPath = keyof typeof RELATED_PAGES;

export function isRelatedPath(s: string): s is RelatedPath {
  return s in RELATED_PAGES;
}

const REVIEWED = "2026-06-01";
/** Guides rewritten and re-checked against their sources when duplicate pages were folded in. */
const V8_REVIEWED = "2026-10-13";
const DISEASE_REVIEWED = "2026-07-20";

export const GUIDES: Guide[] = [
  {
    slug: "alpha-gal-symptoms",
    topic: "Alpha-Gal",
    metaTitle: "Alpha-Gal Syndrome Symptoms: Delayed Meat Allergy Signs",
    metaDescription:
      "Alpha-gal symptoms often start hours after eating mammal meat. They can include hives, stomach upset, swelling, and rarely anaphylaxis. Learn when to get care.",
    h1: "Alpha-Gal Syndrome Symptoms",
    eyebrow: "Alpha-gal • Symptoms",
    keywords: [
      "alpha-gal symptoms",
      "meat allergy symptoms",
      "delayed allergic reaction",
      "AGS signs",
    ],
    intro:
      "Alpha-gal syndrome (AGS) symptoms are often delayed. They often show up about 2–6 hours after eating red (mammal) meat or other products made from mammals. This delay is one reason AGS is hard to spot. The list below is for learning only. It is not a diagnosis, and only a licensed doctor can confirm AGS.",
    sections: [
      {
        h: "Possible symptoms",
        points: [
          "Hives, itching, or itchy, blotchy skin",
          "Swelling of the lips, face, tongue, or throat",
          "Stomach pain, nausea, diarrhea, or vomiting (gut symptoms can be strong)",
          "Runny nose, sneezing, or headaches",
          "Shortness of breath or wheezing",
          "Anaphylaxis: a severe, whole-body reaction (a medical emergency)",
        ],
      },
      {
        h: "Why the delay matters",
        body: "Many food allergies cause a reaction within minutes. AGS reactions tend to come several hours later. Because of this, people may not link a reaction at night to a meal they ate earlier that day. A simple food and symptom log can help you and your doctor spot patterns.",
      },
      {
        h: "Emergency warning signs",
        body: "Call 911 right away for signs of a severe allergic reaction. These include trouble breathing, a swollen throat or tongue, a sudden drop in blood pressure, fainting, or a reaction that spreads fast. Do not wait to see if it gets better.",
      },
      {
        h: "What a clinician can do",
        body: "Only a licensed doctor can diagnose AGS. They look at your history of symptoms and may order certain blood tests. They can also help you manage reactions and know what to avoid. Do not start or stop any treatment based on this page.",
      },
    ],
    faq: [
      {
        q: "How long after eating meat do alpha-gal symptoms start?",
        a: "Usually about 2–6 hours after eating mammal meat. Timing varies from person to person.",
      },
      {
        q: "Are alpha-gal symptoms always severe?",
        a: "No. Symptoms range from mild, such as hives or stomach upset, to severe anaphylaxis. Any severe reaction is a medical emergency. Call 911.",
      },
      {
        q: "Should I stop eating red meat if I think I have AGS?",
        a: "Ask a doctor first, before you make any health choices. This page is for learning. It cannot diagnose you or give you a treatment plan.",
      },
    ],
    sourceIds: ["cdc-ags", "cdc-ags-diagnosis", "ncbi-ags-review"],
    relatedSlugs: ["/alpha-gal", "tick-bite-meat-allergy", "tick-bite-aftercare"],
    lastReviewed: REVIEWED,
  },
  {
    slug: "tick-bite-meat-allergy",
    topic: "Alpha-Gal",
    metaTitle: "Tick Bite & Meat Allergy: The Alpha-Gal Connection",
    metaDescription:
      "Can a tick bite cause a meat allergy? Learn how Lone Star tick bites are linked to alpha-gal syndrome, a delayed red meat allergy, and what to do next.",
    h1: "Can a Tick Bite Cause a Meat Allergy?",
    eyebrow: "Alpha-gal • Tick link",
    keywords: [
      "tick bite meat allergy",
      "tick meat allergy",
      "alpha-gal tick",
      "red meat allergy tick",
    ],
    intro:
      "Yes. In the United States, a bite from the Lone Star tick has been linked to alpha-gal syndrome (AGS), a delayed allergy to red (mammal) meat. This does not happen to everyone who is bitten, and the science is still growing. The best protection is to prevent tick bites in the first place.",
    sections: [
      {
        h: "How the connection works",
        body: "Researchers have linked certain tick bites to an immune response against alpha-gal, a sugar found in mammal meat. In the US, this is mainly true of the Lone Star tick. Once they become sensitized (allergic), some people react when they later eat red meat or other mammal products.",
      },
      {
        h: "Key points to understand",
        points: [
          "Most tick bites do not lead to a meat allergy.",
          "When reactions happen, they usually come several hours after eating.",
          "Repeated tick bites may increase or keep up sensitivity in people who have AGS.",
          "Avoiding tick bites is the most practical way to prevent it.",
        ],
      },
      {
        h: "What to do if you're concerned",
        body: "If you have reactions a few hours after eating red meat, keep a simple log of what you ate and your symptoms. Share it with a licensed doctor. A doctor decides on the diagnosis and any testing. For severe reactions like trouble breathing, call 911.",
      },
    ],
    faq: [
      {
        q: "Which tick causes meat allergy in the U.S.?",
        a: "Mainly the Lone Star tick. It is the species most linked to alpha-gal syndrome in the United States, though research goes on.",
      },
      {
        q: "How soon after a tick bite could a meat allergy appear?",
        a: "It can take weeks to develop. Reactions to meat are also delayed, coming several hours after eating. A doctor can help you make sense of your situation.",
      },
    ],
    sourceIds: ["cdc-ags", "cdc-tick-geography", "ncbi-ags-review"],
    relatedSlugs: ["/alpha-gal", "alpha-gal-symptoms", "lone-star-tick-bite"],
    lastReviewed: REVIEWED,
  },
  {
    slug: "lone-star-tick-bite",
    topic: "After a Bite",
    metaTitle: "Lone Star Tick Bite: What to Know & What to Do",
    metaDescription:
      "What does a Lone Star tick bite look like, and what should you do? Learn how to remove the tick, clean the bite, watch for symptoms, and when to get care.",
    h1: "Lone Star Tick Bite: What to Know and Do",
    eyebrow: "Tick bites • Guide",
    keywords: ["lone star tick bite", "tick bite", "tick bite what to do", "tick bite symptoms"],
    intro:
      "If you find a Lone Star tick bite, the most important steps are simple. Remove the tick quickly and correctly, clean the area, and watch for symptoms over the next days and weeks. Most tick bites do not cause illness. But it helps to know what to do, and when to get care.",
    sections: [
      {
        h: "What to do right now",
        points: [
          "Remove the tick with fine-tipped tweezers, pulling steadily upward.",
          "Clean the bite area and your hands with soap and water or alcohol.",
          "Save the tick (in a sealed bag or a photo) in case you need to show a doctor.",
          "Note the date and where you think you were exposed.",
          "Watch for symptoms over the next several weeks.",
        ],
      },
      {
        h: "What a bite can look like",
        body: "A tick bite often looks like a small red bump. Some people get some irritation or a reaction around the bite. A bite mark alone does not tell you whether an illness will follow. Symptoms in the days and weeks after the bite matter most.",
      },
      {
        h: "Symptoms to watch for",
        points: [
          "A rash or red ring near the bite that grows",
          "Fever, chills, tiredness, or body aches",
          "Hives, swelling, or stomach pain hours after eating red meat",
          "Trouble breathing or anaphylaxis (call 911)",
        ],
      },
      {
        h: "When to contact a healthcare professional",
        body: "Contact a licensed doctor if you get a rash, fever, unusual tiredness, or any delayed reaction after eating mammal meat. Bring your tick photo and notes. For emergency symptoms such as trouble breathing, call 911 right away.",
      },
    ],
    faq: [
      {
        q: "How do I remove a Lone Star tick safely?",
        a: "Use fine-tipped tweezers. Grasp the tick close to the skin and pull steadily upward without twisting. Then clean the area. Avoid folk methods like burning the tick or using petroleum jelly.",
      },
      {
        q: "Should I save the tick?",
        a: "Yes, if you can. A saved tick (in a sealed bag) or a clear photo can help a doctor if you get symptoms. Note the date and the likely place you were exposed.",
      },
      {
        q: "Do all Lone Star tick bites cause illness?",
        a: "No. Most tick bites do not lead to illness. Still, watch for symptoms and contact a doctor if any develop.",
      },
    ],
    sourceIds: ["cdc-remove-tick", "cdc-prevent-bites", "cdc-ags"],
    relatedSlugs: ["tick-bite-aftercare", "/tick-identification", "alpha-gal-symptoms"],
    lastReviewed: REVIEWED,
  },
  {
    slug: "tick-bite-aftercare",
    topic: "After a Bite",
    metaTitle: "Tick Bite Aftercare: Steps After Removing a Tick",
    metaDescription:
      "Tick bite aftercare made simple: clean the area, save the tick, note the date, watch for symptoms, and know when to call a doctor. Not medical advice.",
    h1: "Tick Bite Aftercare",
    eyebrow: "Tick bites • Aftercare",
    keywords: [
      "tick bite aftercare",
      "after tick removal",
      "tick bite care",
      "tick bite follow up",
    ],
    intro:
      "After you remove a tick, good aftercare means three things. Keep the area clean, write down what happened, and watch for symptoms. This page is for learning. For medical questions, contact a licensed doctor.",
    sections: [
      {
        h: "Right after removal",
        points: [
          "Wash the bite area and your hands with soap and water or rubbing alcohol.",
          "Don't crush the tick with your fingers. Seal it in a bag or flush it.",
          "Take a clear photo of the tick and the bite for your records.",
        ],
      },
      {
        h: "Document it",
        points: [
          "Note the date and time you found the tick.",
          "Write down where on your body it was attached.",
          "Note where you think the bite happened (yard, trail, field).",
        ],
      },
      {
        h: "Watch for symptoms",
        body: "Over the next several weeks, watch for a rash, fever, tiredness, joint aches, or any delayed reaction after eating red meat. Keep your notes handy. Most bites cause no illness. But your notes help if you do need to see a doctor.",
      },
      {
        h: "When to seek care",
        body: "Contact a licensed doctor if symptoms appear. For severe allergic symptoms (trouble breathing, a swollen throat, fainting), call 911 right away.",
      },
    ],
    faq: [
      {
        q: "Should I put anything on a tick bite?",
        a: "Just soap and water or alcohol. Keep it clean, and avoid home remedies that are not proven. Ask a doctor about anything beyond basic cleaning.",
      },
      {
        q: "How long should I watch for symptoms?",
        a: "Several weeks. Some reactions, including delayed meat allergy, can take time to appear. Contact a doctor if anything develops.",
      },
    ],
    sourceIds: ["cdc-remove-tick", "cdc-prevent-bites", "cdc-tickborne"],
    relatedSlugs: ["lone-star-tick-bite", "alpha-gal-symptoms", "/tick-identification"],
    lastReviewed: REVIEWED,
  },
  {
    slug: "what-does-a-lone-star-tick-look-like",
    topic: "Identification",
    metaTitle: "What Does a Lone Star Tick Look Like? Photos & Features",
    metaDescription:
      "What does a Lone Star tick look like? The adult female has a single white dot on a reddish-brown body. Learn its size, color, and markings at each stage.",
    h1: "What Does a Lone Star Tick Look Like?",
    eyebrow: "Identification • Appearance",
    keywords: [
      "what does a lone star tick look like",
      "lone star tick appearance",
      "white spot tick",
      "tick with white dot",
    ],
    intro:
      "A Lone Star tick is reddish-brown and rounded. The adult female has one clear white dot in the center of her back. Males have faint white streaks or spots around the edges instead. Younger stages are much smaller and harder to recognize.",
    sections: [
      {
        h: "At a glance",
        points: [
          "Adult female: reddish-brown with a single bright white or silvery dot.",
          "Adult male: smaller marks or streaks near the edge of the body.",
          "Nymph: tiny (about the size of a poppy seed), pale, and easy to miss.",
          "Engorged ticks look larger and grayish after feeding.",
        ],
      },
      {
        h: "Size comparison",
        body: "Unfed adults are about the size of a sesame seed, and nymphs are far smaller. After feeding, ticks swell a lot. Because nymphs are so small, daily tick checks are important during tick season.",
      },
      {
        h: "Could it be a different tick?",
        body: "Blacklegged (deer) ticks are smaller and darker, with no white dot. American dog ticks are larger, with mottled patterns. If you are not sure, save the tick and take a photo so it can be identified.",
      },
    ],
    faq: [
      {
        q: "Is the white dot always visible?",
        a: "No. The single white dot is found only on the adult female. Males and young ticks don't have it, so a missing dot doesn't rule out a Lone Star tick.",
      },
      {
        q: "How big is a Lone Star tick?",
        a: "Unfed adults are about the size of a sesame seed. Nymphs are much smaller. Both swell a lot after feeding.",
      },
    ],
    sourceIds: ["cdc-tick-geography", "tickencounter"],
    relatedSlugs: [
      "/tick-identification",
      "lone-star-tick-vs-deer-tick",
      "where-do-lone-star-ticks-live",
    ],
    lastReviewed: REVIEWED,
  },
  {
    slug: "lone-star-tick-vs-deer-tick",
    topic: "Identification",
    metaTitle: "Lone Star Tick vs Deer Tick: Differences & Why It Matters",
    metaDescription:
      "Lone Star tick vs deer tick: how to tell them apart by size, color, and markings, and why it matters for alpha-gal syndrome and Lyme disease awareness.",
    h1: "Lone Star Tick vs. Deer Tick",
    eyebrow: "Identification • Comparison",
    keywords: [
      "lone star tick vs deer tick",
      "deer tick vs lone star tick",
      "blacklegged tick",
      "tick comparison",
    ],
    intro:
      "Lone Star ticks and deer (blacklegged) ticks look different and are linked to different concerns. Lone Star ticks are reddish-brown, and the female has a white dot. They are linked to alpha-gal syndrome awareness. Deer ticks are smaller and darker. In much of the US, they are the species most linked to Lyme disease awareness.",
    sections: [
      {
        h: "Quick comparison",
        points: [
          "Lone Star tick: reddish-brown and rounded; the adult female has a single white dot.",
          "Deer (blacklegged) tick: smaller and dark, with no white dot; females have a reddish rear.",
          "Lone Star ticks are aggressive biters; deer ticks move more slowly.",
          "Different species are linked to different health concerns.",
        ],
      },
      {
        h: "Why the difference matters",
        body: "Knowing which tick you found helps you know what to watch for. In the US, Lone Star ticks are the species most linked to alpha-gal syndrome. Deer ticks are most linked to Lyme disease awareness. Either way, remove the tick quickly and watch for symptoms.",
      },
      {
        h: "When you can't tell",
        body: "It is hard to tell ticks apart, especially tiny nymphs. Save the tick, take a photo, and check with your state health department or a university extension program. This page is for learning and is not a diagnosis.",
      },
    ],
    faq: [
      {
        q: "Which tick causes Lyme disease?",
        a: "In much of the US, Lyme disease awareness centers on the blacklegged (deer) tick. The Lone Star tick is linked more with alpha-gal syndrome. A doctor should check any illness.",
      },
      {
        q: "Can both ticks be in the same area?",
        a: "Yes. Their ranges overlap in many regions, so you may find more than one species. The same steps to prevent bites protect against all of them.",
      },
    ],
    sourceIds: ["cdc-tick-geography", "cdc-tickborne", "tickencounter"],
    relatedSlugs: [
      "/tick-identification",
      "what-does-a-lone-star-tick-look-like",
      "where-do-lone-star-ticks-live",
    ],
    lastReviewed: REVIEWED,
  },
  {
    slug: "ticks-on-dogs",
    topic: "Pets & Yard",
    metaTitle: "Ticks on Dogs: Prevention, Checks & Safe Removal",
    metaDescription:
      "How to protect dogs from ticks: prevention your vet recommends, where to check, and how to remove a tick safely. Educational guidance. Ask your vet.",
    h1: "Ticks on Dogs: Prevention and Removal",
    eyebrow: "Pets & yard • Dogs",
    keywords: ["ticks on dogs", "dog tick prevention", "remove tick from dog", "dog tick check"],
    intro:
      "Dogs are easy targets for ticks, and they can bring ticks into your home. The best protection is a tick product your vet recommends, plus regular tick checks after time outdoors. Talk to your vet about which product to use and about any health concerns.",
    sections: [
      {
        h: "Prevention basics",
        points: [
          "Ask your vet about tick prevention products that suit your dog.",
          "Check your dog after walks, hikes, and yard time during tick season.",
          "Keep grass trimmed and cut back brush where dogs roam.",
          "Never use human repellents on pets unless your vet says it's OK.",
        ],
      },
      {
        h: "Where to check",
        points: [
          "Around the ears and inside the ear folds",
          "Under the collar and around the neck",
          "Between the toes and around the paws",
          "Under the legs, at the base of the tail, and around the groin",
        ],
      },
      {
        h: "Safe removal",
        body: "Use fine-tipped tweezers to grasp the tick close to the skin, and pull steadily upward. Clean the area afterward. If you're unsure, or your dog seems unwell, contact your vet.",
      },
    ],
    faq: [
      {
        q: "Can dogs get alpha-gal syndrome?",
        a: "This page focuses on people. Ask your vet about your dog's health and the right products to prevent ticks.",
      },
      {
        q: "How often should I check my dog for ticks?",
        a: "After each outing during tick season, especially after time in woods or tall grass. Daily checks make sense when ticks are most active.",
      },
    ],
    sourceIds: ["cdc-prevent-bites", "cdc-remove-tick", "epa-permethrin"],
    relatedSlugs: ["tick-proof-yard", "lone-star-tick-bite", "tick-safety-for-hikers"],
    lastReviewed: V8_REVIEWED,
  },
  {
    slug: "tick-proof-yard",
    topic: "Pets & Yard",
    metaTitle: "Tick-Proof Your Yard: Practical Steps to Reduce Ticks",
    metaDescription:
      "How to make your yard less friendly to ticks: mow often, clear leaf litter, add barriers, and give wildlife fewer places to shelter. Practical yard tips.",
    h1: "How to Tick-Proof Your Yard",
    eyebrow: "Pets & yard • Yard",
    keywords: ["tick proof yard", "tick proof your yard", "reduce ticks yard", "tick yard control"],
    intro:
      "A few simple habits can make your yard much less inviting to ticks. Keep grass short, clear leaf litter and brush, and make a dry barrier between lawn and woods. These steps cut down the shady, damp spots where ticks wait for a host.",
    sections: [
      {
        h: "Reduce tick habitat",
        points: [
          "Mow the lawn often and keep grass short.",
          "Remove leaf litter, brush piles, and tall weeds.",
          "Make a 3-foot barrier of wood chips or gravel between lawn and woods.",
          "Keep play sets and seating away from yard edges and shade.",
        ],
      },
      {
        h: "Discourage wildlife harborage",
        points: [
          "Stack woodpiles neatly in dry, sunny spots.",
          "Secure trash so fewer animals that carry ticks come by.",
          "Think about plants that deer don't like near the house.",
        ],
      },
      {
        h: "Personal protection still matters",
        body: "Yard steps cut down ticks, but they don't get rid of them. Keep using EPA-registered repellents, and check yourself, your children and your pets for ticks after time in the yard, especially in peak season.",
      },
    ],
    faq: [
      {
        q: "Do yard sprays work?",
        a: "Results vary. Some people use professional treatments. Any product should be used the way its label says. Cutting back tick habitat and protecting yourself are still the base of a good plan.",
      },
      {
        q: "Where do ticks hide in a yard?",
        a: "In shady, damp spots: leaf litter, tall grass, and the brushy border between lawn and woods. Drying out these areas helps.",
      },
    ],
    sourceIds: ["cdc-prevent-bites", "epa-permethrin", "tickencounter"],
    relatedSlugs: ["ticks-on-dogs", "tick-safety-for-hikers", "where-do-lone-star-ticks-live"],
    lastReviewed: V8_REVIEWED,
  },
  {
    slug: "tick-safety-for-hikers",
    topic: "Outdoor Activities",
    metaTitle: "Tick Safety for Hikers: Stay Protected on the Trail",
    metaDescription:
      "Tick safety for hikers: repellent, permethrin-treated clothing, trail habits, and tick checks after your hike to cut bites on the trail.",
    h1: "Tick Safety for Hikers",
    eyebrow: "Outdoors • Hikers",
    keywords: [
      "tick safety for hikers",
      "hiking ticks",
      "prevent ticks hiking",
      "trail tick prevention",
    ],
    intro:
      "Hikers spend time right where ticks wait: along grassy, brushy trail edges. A simple routine greatly lowers your chance of a bite. Use repellent, wear treated clothing, follow smart trail habits, and check yourself well for ticks after each hike.",
    sections: [
      {
        h: "Before you hike",
        points: [
          "Use an EPA-registered repellent (DEET, picaridin, or oil of lemon eucalyptus).",
          "Treat clothing, socks, and gear with permethrin (follow the label).",
          "Wear light-colored clothing so ticks are easier to spot.",
          "Tuck pants into socks on brushy or overgrown trails.",
          "Pack fine-tipped tweezers and a small sealed bag, so you can take off a tick and keep it.",
        ],
      },
      {
        h: "On the trail",
        points: [
          "Walk in the center of the trail; stay out of tall grass and brush.",
          "Take breaks on rocks or bare ground, not on logs or leaf litter.",
        ],
      },
      {
        h: "After your hike",
        points: [
          "Check your whole body for ticks, including the scalp, waistband, and behind the knees.",
          "Shower within two hours of getting home.",
          "Tumble clothes on high heat to kill any ticks that hitched a ride.",
        ],
      },
    ],
    faq: [
      {
        q: "What repellent is best for hiking?",
        a: "Common choices are EPA-registered repellents such as DEET, picaridin, or oil of lemon eucalyptus. Use the EPA's search tool to find one that fits your needs, and follow the label.",
      },
      {
        q: "Does permethrin go on skin?",
        a: "No. Permethrin is for clothing and gear, not skin. Apply it as the label says, and let it dry before you wear the item.",
      },
    ],
    sourceIds: ["cdc-prevent-bites", "cdc-remove-tick", "epa-repellent-search", "epa-permethrin"],
    relatedSlugs: ["tick-safety-for-hunters", "tick-proof-yard", "lone-star-tick-bite"],
    lastReviewed: V8_REVIEWED,
  },
  {
    slug: "tick-safety-for-hunters",
    topic: "Outdoor Activities",
    metaTitle: "Tick Safety for Hunters: Field Protection & Checks",
    metaDescription:
      "Tick safety for hunters, who face high exposure: permethrin-treated gear, repellent, care when field-dressing, and full tick checks to lower the risk.",
    h1: "Tick Safety for Hunters",
    eyebrow: "Outdoors • Hunters",
    keywords: [
      "tick safety for hunters",
      "hunting ticks",
      "hunter tick prevention",
      "field tick protection",
    ],
    intro:
      "Hunters are often around a lot of ticks. They sit in brush, walk through fields, and handle game. Permethrin-treated gear, repellent, and careful checks together help lower the chance of bites during long days in the field.",
    sections: [
      {
        h: "Gear up",
        points: [
          "Treat hunting clothes and boots with permethrin before the season.",
          "Use an EPA-registered repellent on bare skin.",
          "Tuck pants into boots and seal gaps where ticks climb on.",
        ],
      },
      {
        h: "In the field",
        points: [
          "Field-dressing game can expose you to ticks, so think about wearing gloves.",
          "Try not to sit right in leaf litter or tall grass.",
        ],
      },
      {
        h: "After the hunt",
        points: [
          "Check your whole body for ticks, including hard-to-see areas.",
          "Shower soon after you get back, and dry clothing on high heat.",
          "Note any bites and watch for symptoms in the weeks that follow.",
          "If you get hives, swelling or stomach pain a few hours after eating venison, beef or pork, ask a doctor about alpha-gal syndrome.",
        ],
      },
    ],
    faq: [
      {
        q: "Why are hunters at higher tick risk?",
        a: "Because they spend long hours where ticks live. They may also handle game, which adds to the risk. Treated gear and careful checks help cut down bites.",
      },
      {
        q: "Can handling deer cause alpha-gal syndrome?",
        a: "Not by itself. Alpha-gal syndrome is linked to tick bites, not to handling game. Cutting down tick bites is the practical goal. For medical questions, see a doctor.",
      },
    ],
    sourceIds: ["cdc-prevent-bites", "epa-permethrin", "epa-repellent-search", "cdc-ags"],
    relatedSlugs: ["tick-safety-for-hikers", "/alpha-gal", "alpha-gal-symptoms"],
    lastReviewed: V8_REVIEWED,
  },
  {
    slug: "where-do-lone-star-ticks-live",
    topic: "Maps & Seasons",
    metaTitle: "Where Do Lone Star Ticks Live? U.S. Range & Habitat",
    metaDescription:
      "Where do Lone Star ticks live? They're established across the southeastern and eastern US and spreading north. Learn the range, habitats, and how to check.",
    h1: "Where Do Lone Star Ticks Live?",
    eyebrow: "Range • Geography",
    keywords: [
      "where do lone star ticks live",
      "lone star tick range",
      "lone star tick map",
      "lone star tick states",
    ],
    intro:
      "Lone Star ticks are established across much of the southeastern and eastern United States. They have been reported spreading north and into the Midwest. They like wooded areas, brushy fields, and the shady edges where lawns meet woods. CDC keeps a record for every US county. Search your ZIP code, town or county on this site to see it.",
    sections: [
      {
        h: "General range",
        body: "The Lone Star tick's range covers much of the Southeast and eastern US. Reports reach into parts of the Midwest and Northeast. Ranges shift over time, and whether the tick is present varies by county.",
      },
      {
        h: "Established or reported?",
        body: "CDC sorts every county into one of three groups. Established means 6 or more ticks, or more than one life stage, were found there in a year. Reported means fewer were found. No records means none were reported to CDC, which does not prove there are none. The count of established counties in each state is in the table above.",
      },
      {
        h: "Preferred habitats",
        points: [
          "Wooded areas and shady trail edges",
          "Tall grass, brush, and overgrown fields",
          "Leaf litter and dense ground cover",
          "Yard borders where lawns meet woods",
        ],
      },
      {
        h: "Tick presence ≠ disease risk",
        body: "Ticks living in an area does not mean a bite will spread disease or cause alpha-gal syndrome. It is a reminder to take steps to prevent bites. Our state pages have local awareness and sources.",
      },
    ],
    faq: [
      {
        q: "Are Lone Star ticks spreading?",
        a: "Reports suggest so. The range seems to have grown over time, including to the north. Whether they are present varies by county, so check state and local health resources for details.",
      },
      {
        q: "How do I check my area?",
        a: "Search your ZIP code, town or county on the home page to see CDC's record for your county. In the states where the tick is found or spreading, each county also has its own page with a map of the counties around it.",
      },
    ],
    sourceIds: [
      "cdc-lst-county-data",
      "cdc-lone-star-surveillance",
      "cdc-tick-surveillance",
      "cdc-tick-geography",
      "tickencounter",
    ],
    relatedSlugs: ["tick-season-by-state", "/tick-identification", "tick-proof-yard"],
    lastReviewed: V8_REVIEWED,
  },
  {
    slug: "tick-season-by-state",
    topic: "Maps & Seasons",
    metaTitle: "Tick Season by State: When Lone Star Ticks Are Active",
    metaDescription:
      "When is tick season? Lone Star ticks are most active from spring through fall, with longer seasons in warmer states. See peak months by state.",
    h1: "Tick Season by State",
    eyebrow: "Seasons • By state",
    keywords: [
      "tick season by state",
      "when is tick season",
      "lone star tick season",
      "tick activity months",
    ],
    intro:
      "Lone Star ticks are most active from spring through fall, with a peak in the warm months. Warmer states can have longer seasons. Peak months differ from state to state. This page lists them for each state where Lone Star ticks are found, with the source for each. Keep in mind that ticks can be active any time it is warm enough.",
    sections: [
      {
        h: "General pattern",
        body: "In much of the Lone Star tick's range, activity rises in spring, peaks in summer, and tapers off in fall. In warmer southern states, ticks may be active for more of the year. Mild winter days can also bring short bursts of activity.",
      },
      {
        h: "Adults, nymphs and larvae",
        body: "Adult Lone Star ticks are active from April to late August, the University of Rhode Island's TickEncounter says. The young stages are much smaller and easy to miss. Larvae, sometimes called seed ticks, latch on by the hundreds, so a cluster of many tiny bites at once may be larvae.",
      },
      {
        h: "Plan prevention around the season",
        points: [
          "Step up repellent and treated clothing during peak months.",
          "Check people and pets for ticks every day in season.",
          "Stay alert on warm days, even outside the usual peak.",
        ],
      },
      {
        h: "Check your state",
        body: "Peak months vary by region. The chart above shows each state where the tick is found, with its source. Your state page has the rest, including local source links.",
      },
    ],
    faq: [
      {
        q: "When is tick season worst?",
        a: "In the warm months. Activity generally peaks from about spring through fall, with longer seasons in warmer states. The chart on this page shows each state where the tick is found.",
      },
      {
        q: "Can ticks be active in winter?",
        a: "Yes, on mild days, even outside peak season. Stay alert whenever it is warm enough for ticks.",
      },
    ],
    sourceIds: [
      "cdc-tick-geography",
      "uri-tickencounter-lst",
      "cdc-prevent-bites",
      "cdc-tickborne",
    ],
    relatedSlugs: ["where-do-lone-star-ticks-live", "tick-safety-for-hikers", "tick-proof-yard"],
    lastReviewed: V8_REVIEWED,
  },
  {
    slug: "tickborne-diseases-lone-star-tick",
    topic: "Diseases",
    metaTitle: "Diseases the Lone Star Tick Can Cause (Beyond Alpha-Gal)",
    metaDescription:
      "The Lone Star tick is linked to more than alpha-gal syndrome: ehrlichiosis, STARI, and, rarely, Heartland virus, Bourbon virus, and tularemia.",
    h1: "Diseases Linked to the Lone Star Tick",
    eyebrow: "Tickborne illness • Overview",
    keywords: [
      "lone star tick diseases",
      "lone star tick illness",
      "tickborne diseases",
      "ehrlichiosis",
      "STARI",
      "heartland virus",
    ],
    intro:
      "The Lone Star tick (Amblyomma americanum) is best known for its link to alpha-gal syndrome. But it can play a part in other illnesses too. The most common is ehrlichiosis. It is also linked to southern tick-associated rash illness (STARI) and, rarely, Heartland virus, Bourbon virus, and tularemia. The Lone Star tick is not thought to spread Lyme disease. This is an overview for learning. Only a licensed doctor can diagnose or treat any of these illnesses.",
    sections: [
      {
        h: "The short version",
        points: [
          "Ehrlichiosis: a bacterial infection and the most common Lone Star tick illness. Antibiotics treat it when given early.",
          "STARI: a rash that can follow a bite and grows over time. Its cause is not known, and it is not Lyme disease.",
          "Alpha-gal syndrome: a delayed allergy to mammal meat that can start after bites.",
          "Heartland & Bourbon viruses: rare viral illnesses. There is no specific antiviral treatment.",
          "Tularemia: a bacterial illness that can spread by tick bites and in other ways.",
        ],
      },
      {
        h: "Ehrlichiosis",
        body: "It is the most often reported illness linked to the Lone Star tick. Early symptoms can include fever, headache, tiredness, and muscle aches. These often start within days to a couple of weeks of a bite. Get checked quickly, because antibiotics can treat it when it is caught early.",
      },
      {
        h: "STARI (southern tick-associated rash illness)",
        body: "STARI causes a rash around a Lone Star tick bite that grows, and it is sometimes shaped like a bull's-eye. Some people also have tiredness, headache, or muscle aches. Its exact cause is not known, and it is not Lyme disease. But a doctor should always check a rash after a tick bite.",
      },
      {
        h: "Alpha-gal syndrome (AGS)",
        body: "AGS is a delayed allergic reaction to alpha-gal, a sugar found in mammal meat. It can start after certain tick bites. Reactions tend to show up hours after eating red meat. See the alpha-gal guides for details.",
      },
      {
        h: "Rarer concerns: Heartland virus, Bourbon virus, tularemia",
        body: "Heartland and Bourbon viruses are rare illnesses linked to Lone Star tick bites. There is no specific antiviral treatment, so doctors treat the symptoms. Tularemia can spread through tick bites and in other ways. These illnesses are uncommon. But severe or lasting symptoms after a bite always need medical care.",
      },
      {
        h: "The best protection is avoiding bites",
        body: "All of these illnesses start the same way, with a tick bite. So the same steps protect against all of them: repellent, permethrin-treated clothing, staying out of tall grass and brush, and daily tick checks. Remove any attached tick quickly and the right way.",
      },
    ],
    faq: [
      {
        q: "Can the Lone Star tick give you Lyme disease?",
        a: "No. The Lone Star tick is not thought to spread Lyme disease. Lyme is spread mainly by blacklegged (deer) ticks. A rash after a Lone Star tick bite may be STARI, which is a different illness. See a doctor about any rash.",
      },
      {
        q: "What is the most common illness from a Lone Star tick?",
        a: "Ehrlichiosis. It is the most often reported bacterial illness linked to the Lone Star tick. Antibiotics can treat it when given early, so get symptoms checked soon after a bite.",
      },
      {
        q: "How soon do symptoms appear after a bite?",
        a: "It depends on the illness. Some symptoms appear within days to a couple of weeks. An alpha-gal allergy can show up later, with reactions hours after eating red meat. Watch for symptoms for several weeks, and contact a doctor if any appear.",
      },
      {
        q: "How do I lower my risk of all of these?",
        a: "Prevent bites. Use EPA-registered repellent and treat clothing with permethrin. Stay out of tall grass and leaf litter, and check people and pets for ticks every day. Remove attached ticks right away with fine-tipped tweezers.",
      },
    ],
    sourceIds: [
      "cdc-tickborne",
      "cdc-ehrlichiosis",
      "cdc-stari",
      "cdc-heartland",
      "cdc-ags",
      "cdc-prevent-bites",
    ],
    relatedSlugs: [
      "/diseases/ehrlichiosis",
      "/diseases/stari",
      "heartland-and-bourbon-virus",
      "/alpha-gal",
    ],
    lastReviewed: DISEASE_REVIEWED,
  },
  {
    slug: "heartland-and-bourbon-virus",
    topic: "Diseases",
    metaTitle: "Heartland & Bourbon Virus: Rare Lone Star Tick Illnesses",
    metaDescription:
      "Heartland and Bourbon viruses are rare illnesses linked to Lone Star tick bites, with no specific antiviral treatment. Learn the symptoms and prevention.",
    h1: "Heartland and Bourbon Virus",
    eyebrow: "Tickborne illness • Explainer",
    keywords: [
      "heartland virus",
      "bourbon virus",
      "lone star tick virus",
      "tickborne virus symptoms",
    ],
    intro:
      "Heartland virus and Bourbon virus are rare illnesses linked to Lone Star tick bites in parts of the United States. Both can cause fever, tiredness, and low blood cell counts. Neither has a specific antiviral treatment, so doctors treat the symptoms. They are uncommon. But severe or lasting symptoms after a tick bite always need to be checked by a doctor quickly. This page is for learning only.",
    sections: [
      {
        h: "The short version",
        points: [
          "Rare viral illnesses linked to Lone Star tick bites.",
          "Symptoms can include fever, tiredness, headache, muscle aches, nausea.",
          "No specific antiviral treatment. Care is supportive.",
          "Prevention (avoiding bites) is the best protection.",
        ],
      },
      {
        h: "Heartland virus",
        body: "Heartland virus can cause fever, tiredness, headache, muscle aches, and low counts of certain blood cells. Most reported cases have been in people who spent a lot of time outdoors where the Lone Star tick lives. No antiviral medicine targets it, so doctors treat the symptoms.",
      },
      {
        h: "Bourbon virus",
        body: "Bourbon virus was first found in the US in 2014. It is very rare. It can cause fever, tiredness, rash, headache, and low blood cell counts. As with Heartland virus, there is no specific treatment, so doctors treat the symptoms.",
      },
      {
        h: "When to seek care and how to prevent bites",
        body: "Get medical care for a high or lasting fever, severe tiredness, or feeling very unwell after a tick bite, and mention the bite. There's no specific treatment, so prevention matters most. Use repellent and permethrin-treated clothing, stay out of tall grass and brush, check for ticks daily, and remove ticks quickly.",
      },
    ],
    faq: [
      {
        q: "How serious are Heartland and Bourbon viruses?",
        a: "Both are rare and vary in how severe they are. Some people have needed hospital care. There is no specific antiviral treatment, so doctors treat the symptoms. Severe or lasting symptoms after a tick bite need to be checked by a doctor quickly.",
      },
      {
        q: "Is there a treatment or vaccine?",
        a: "No. There is no specific antiviral treatment and no vaccine for either virus. A licensed doctor guides care, which treats the symptoms. That is why preventing tick bites comes first.",
      },
      {
        q: "How can I protect myself?",
        a: "Avoid bites. Use EPA-registered repellent and treat clothing with permethrin. Stay out of tall grass and leaf litter, check for ticks every day, and remove attached ticks right away.",
      },
    ],
    sourceIds: ["cdc-heartland", "cdc-bourbon", "cdc-tickborne", "cdc-prevent-bites"],
    relatedSlugs: [
      "tickborne-diseases-lone-star-tick",
      "/diseases/ehrlichiosis",
      "tick-safety-for-hunters",
    ],
    lastReviewed: DISEASE_REVIEWED,
  },
];

export const getGuide = (slug: string) => GUIDES.find((g) => g.slug === slug);
